<template>
    <b-modal
        id="sub-assignment-feedback-details-modal"
        :title="`${subAssignmentIn.name} feedback overzicht`"
        modal-class="apprentice-default-modal"
        ok-only
        @show="onShow"
        :ok-title="$t('common.close')"
        size="lg">
        <div class="feedback-history apprentice-messages-widget">
            <div
                class="feedback-list"
                v-if="subAssignmentIn.feedback.length >= 1">
                <div
                    class="message-item"
                    v-for="feedback in subAssignmentIn.feedback"
                    :key="`sub-assignment-feedback-${feedback.id}`"
                    :class="[feedback.user_id === $authUser.id ? 'left' : 'right', `sub-message-row-${feedback.id}`]">
                    <div class="feedback-item">
                        <div class="user-info">
                            <div class="credentials">{{ feedback.user.full_name }}</div>
                            <div class="date">{{ feedback.created_at | date_time_om }}</div>
                            <div
                                class="context-nav sub-assignment-feedback-edit-context-nav"
                                v-if="!$authUser.isStudent() && feedback.user_id === $authUser.id">
                                <b-dropdown
                                    class="ellipsis-nav sub-assignment-feedback-edit-context-menu bordered-menu"
                                    variant="primary"
                                    no-caret
                                    right>
                                    <template #button-content>
                                        <i class="fas fa-fw fa-ellipsis-v"></i>
                                    </template>
                                    <b-dropdown-item
                                        class="btn-sub-assignment-feedback-edit"
                                        @click="editFeedback(feedback)">
                                        <i class="btn-edit"></i>Bewerken
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        class="btn-sub-assignment-feedback-remove"
                                        @click="removeFeedback(feedback)">
                                        <i class="btn-remove"></i>Verwijderen
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                        <div class="message-block">
                            <div
                                class="message-content"
                                :class="canEdit & (feedback.id === currentFeedback.id) && 'edit-state'">
                                <div class="arrow"></div>
                                <b-collapse
                                    class="feedback-textcontent"
                                    :id="`feedback-sub-assignment-block-${feedback.id}`">
                                    <design-2021-apprentice-display-html-component
                                        v-if="!canEdit || feedback.id !== currentFeedback.id"
                                        :html-content="feedback.text">
                                    </design-2021-apprentice-display-html-component>

                                    <div
                                        class="edit-feedback-message"
                                        v-else-if="canEdit && feedback.id === currentFeedback.id">
                                        <ckeditor
                                            v-model="currentFeedback.text"
                                            :editor="editor"
                                            :config="editorConfig"
                                            :key="`sub-editor-${feedback.id}`"
                                            @ready="onEditorReady($event, feedback)"
                                            placeholder="Voer feedback in">
                                        </ckeditor>
                                    </div>
                                </b-collapse>
                                <div
                                    class="more-and-less"
                                    v-if="hasContentToPreview(feedback.text, 50) && !canEdit">
                                    <b-button
                                        v-b-toggle="`feedback-sub-assignment-block-${feedback.id}`"
                                        variant="link-type">
                                        <span class="when-open btn-show-less">Verberg</span>
                                        <span class="when-closed btn-show-more">Toon meer</span>
                                    </b-button>
                                </div>
                            </div>
                            <div class="avatar">
                                <b-avatar
                                    variant="primary"
                                    size="3rem"
                                    :src="getProfileImage(feedback)">
                                </b-avatar>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <design-2021-assignment-show-compose-sub-assignment-feedback-component
                v-if="!$authUser.isStudent() && !canEdit"
                ref="createFeedback"
                :sub-assignment-in="subAssignmentIn"
                :project-id="projectId"
                :aria-disabled="canEdit"
                :show-store-button="false"
                @store-completed="onStoreCompleted">
            </design-2021-assignment-show-compose-sub-assignment-feedback-component>
        </div>

        <template #modal-footer="{ ok, cancel }">
            <b-button
                class="btn-redo btn-cancel-feedback-update"
                variant="outline-primary"
                @click="onCancel()">
                Annuleren
            </b-button>

            <b-button
                class="btn-save btn-update-feedback"
                v-if="canEdit"
                variant="primary"
                @click="updateFeedback(currentFeedback)">
                Opslaan
            </b-button>

            <b-button
                class="btn-save btn-add-feedback"
                v-if="!canEdit"
                variant="primary"
                @click="storeFeedback()">
                Opslaan
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../../../helpers/ApprenticeEditor';

function foreReload(callback) {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
}

export default {
    name: 'design-2021-assignment-show-sub-assignment-feedback-details-model-component',
    props: {
        subAssignmentIn: Object,
        projectId: Number,
    },
    data() {
        return {
            currentFeedback: '',
            canEdit: false,
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'heading',
                        'fontBackgroundColor',
                        '|',
                        'bold',
                        'italic',
                        'link',
                        'bulletedList',
                        'numberedList',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
            initialText: '',
        };
    },
    methods: {
        onShow() {
            this.cancelFeedbackUpdate();
        },

        getProfileImage(message) {
            if (message.user.profile_image_thumb_path) {
                return message.user.profile_image_thumb_path;
            } else if (this.profileImageIn && this.profileImageIn.file) {
                return this.profileImageIn.file.public_path;
            }

            return '/images/default_avatar_profile.jpg';
        },

        hasContentToPreview(text, maxChar) {
            let feedbackText = text;
            if (text.lastIndexOf(`<p>&nbsp;</p>`)) {
                feedbackText = text.slice(0, text.lastIndexOf(`<p>&nbsp;</p>`));
            }

            const previewText = this.$options.filters.strip_html_tags(text);
            return (
                feedbackText.includes('<table>') ||
                feedbackText.includes('<ul>') ||
                feedbackText.includes('<ol>') ||
                feedbackText.includes('<br>') ||
                feedbackText.includes('&nbsp;') ||
                previewText.length > maxChar
            );
        },

        removeFeedback(feedback) {
            this.alert
                .confirm('Feedback verwijderen', 'Weet je zeker dat je de feedback wil verwijderen?')
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.assignmentFeedbackRemove(feedback.id)).then(
                        (response) => {
                            feedback.remove();
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },

        editFeedback(feedback) {
            this.currentFeedback = feedback;
            this.initialText = feedback.text;
            this.canEdit = true;

            foreReload(() => {
                $(`.sub-message-row-${feedback.id}`).get(0).scrollIntoView();
            });
        },

        updateFeedback(feedback) {
            const data = {
                text: this.currentFeedback.text,
            };

            this.alert.saving();
            this.axios.post(this.routes.assignmentFeedbackUpdate(feedback.id), data).then(
                (response) => {
                    feedback.update(response.data.assignment);
                    if (!feedback.user) {
                        feedback.addRelation('user', response.data.user);
                    } else if (feedback.user.id !== response.data.user.id) {
                        feedback.detach('user', this.assignmentIn.feedback.user.id);
                        feedback.addRelation('user', response.data.user);
                    }
                    this.canEdit = false;
                    this.currentFeedback = '';
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        cancelFeedbackUpdate(feedback) {
            if (feedback) {
                feedback.text = this.initialText;
            }
            this.canEdit = false;
            this.currentFeedback = '';
        },

        onEditorReady(event, feedback) {
            foreReload(() => {
                event.focus();
            });
        },

        storeFeedback() {
            this.$refs.createFeedback.storeFeedback();
        },

        onStoreCompleted() {
            this.$bvModal.hide('sub-assignment-feedback-details-modal');
        },

        onCancel() {
            if (this.canEdit) {
                this.canEdit = false;
            } else {
                this.$bvModal.hide('sub-assignment-feedback-details-modal');
            }
        },
    },
};
</script>

<style scoped lang="scss">
.collapsed > .when-open,
.not-collapsed > .when-closed {
    display: none;
}

.feedback-history {
    .feedback-list {
        min-height: 140px;
        max-height: 450px;
        height: 100%;
        overflow-y: auto;
        margin-bottom: $grid-gutter-width;

        &:last-child {
            margin-bottom: 9px;
        }
    }

    .credentials,
    .date {
        font-weight: $font-weight-bold;
    }

    .credentials {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        line-height: 1.3;
        flex: 1;
        align-items: center;
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }

    .date {
        align-self: center;
        color: $text-muted-color;
        margin-left: map-get($grid-gutter-spacing, 'fifteen');
    }

    div.feedback-item {
        div.message-block {
            div.textmessage {
                margin-left: 0;
                padding: 0;
                border-radius: 0;
                background-color: transparent;
                width: 100%;
            }
        }
    }

    div.message-block {
        margin-top: map-get($grid-gutter-spacing, 'ten');
        flex-direction: row-reverse;

        .message-content {
            position: relative;
            display: flex;
            flex: 1;
            flex-wrap: nowrap;
            flex-direction: column;
            width: calc(100% - 20px);
            background-color: $secondary-background-color;
            padding: map-get($grid-gutter-spacing, 'fifteen');
            border-top-right-radius: $border-radius-content;
            border-bottom-right-radius: $border-radius-content;
            border-bottom-left-radius: $border-radius-content;

            &:before {
                border-top: 1em solid $secondary-background-color !important;
            }

            .feedback-textcontent {
                display: block !important;
                height: 45px;
                overflow: hidden;

                .display-html-content {
                    overflow-x: auto;
                }

                &.collapsing {
                    min-height: 45px;
                    transition: height 0.8s ease;
                }

                &.show {
                    height: 100%;
                }
            }

            .more-and-less {
                display: flex;
                justify-content: flex-end;
                margin-top: map-get($grid-gutter-spacing, 'twenty-one');

                span {
                    font-weight: $font-weight-bold;

                    &:after {
                        padding-top: 0;
                    }
                }

                i {
                    padding-top: 0;
                }
            }
        }

        .message-content.edit-state {
            padding-left: 0;
            padding-right: 0;
            padding-top: 0;
            background-color: $white;

            &:before {
                border-top: 1em solid $white !important;
            }

            .feedback-textcontent {
                height: auto;
            }

            ::v-deep .ck-editor__main {
                height: 160px;
            }
        }

        .avatar {
            display: none;
        }
    }

    .feedback-item:not(:last-child) {
        .edit-feedback-message {
            margin-bottom: $grid-gutter-width;
            padding-bottom: $grid-gutter-width;
            border-bottom: 3px dotted $border-color;
        }
    }

    .edit-feedback-message {
        .actions {
            margin-top: map-get($grid-gutter-spacing, 'forty-two');
            display: flex;
            align-items: center;
            justify-content: flex-start;

            .btn {
                &:not(:last-child) {
                    margin-right: $grid-gutter-width;
                }
            }
        }
    }

    .compose-feedback {
        padding-top: $grid-gutter-width;

        &:not(:first-child) {
            border-top: 3px dotted $border-color;
        }
    }
}

.edit-feedback-message ::v-deep {
    .ck-content {
        width: 99%;
        margin: 0 auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .feedback-history {
        .feedback-item {
            min-height: 145px;
        }

        div.message-block {
            .message-content {
                .feedback-textcontent {
                    height: 25px;

                    &.collapsing {
                        min-height: 25px;
                    }
                }
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .feedback-history {
        div.message-block {
            .avatar {
                display: flex;
            }

            .message-content {
                margin-left: 20px;
                border-top-left-radius: 0;

                &:before {
                    content: '';
                    display: block;
                    position: absolute;
                    top: 0;
                    border-top: 1em solid $secondary-background-color !important;
                    border-left: 1em solid transparent;
                    left: -13px;
                }
            }
        }
    }
}
</style>
